// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="Navbar" set="mytaible-shadcn-studio" verified=tbd
import * as React from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../atoms/button';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from '../molecules/navigation-menu';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface NavbarLink {
  label: string;
  href: string;
  active?: boolean;
}

export interface NavbarProps extends React.HTMLAttributes<HTMLElement> {
  layout?:
    | 'withCta'
    | 'withCenteredLogo'
    | 'withSearchInput'
    | 'multipleAction'
    | 'small'
    | 'activeWithBorder';
  logo?: React.ReactNode;
  links?: NavbarLink[];
  actions?: React.ReactNode;
}

// ─── Navbar ───────────────────────────────────────────────────────────────────

const Navbar = React.forwardRef<HTMLElement, NavbarProps>(
  ({ className, layout = 'withCta', logo, links = [], actions, ...props }, ref) => {
    const isSmall = layout === 'small';
    const isCentered = layout === 'withCenteredLogo';

    return (
      <header
        ref={ref}
        className={cn(
          'sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60',
          className,
        )}
        {...props}
      >
        <div
          className={cn(
            'flex items-center justify-between px-4',
            isSmall ? 'h-12' : 'h-16',
            isCentered && 'relative',
          )}
        >
          {/* Logo — centered or left */}
          {isCentered ? (
            <>
              <div className="flex-1" />
              {logo && <div className="absolute left-1/2 -translate-x-1/2">{logo}</div>}
            </>
          ) : (
            logo && <div className="mr-4 shrink-0">{logo}</div>
          )}

          {/* Navigation links */}
          {links.length > 0 && (
            <NavigationMenu className={cn(isCentered && 'flex-1 justify-start')}>
              <NavigationMenuList>
                {links.map((link) => (
                  <NavigationMenuItem key={link.href}>
                    <NavigationMenuLink
                      href={link.href}
                      className={cn(
                        'inline-flex h-10 w-max items-center justify-center rounded-[var(--radius)] px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none',
                        link.active &&
                          (layout === 'activeWithBorder'
                            ? 'border-b-2 border-primary rounded-none text-foreground'
                            : 'bg-accent/50 text-foreground'),
                        !link.active && 'text-muted-foreground',
                      )}
                    >
                      {link.label}
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
          )}

          {/* Actions slot */}
          {actions ? (
            <div className="ml-auto flex items-center gap-2">{actions}</div>
          ) : layout === 'withCta' ? (
            <div className="ml-auto">
              <Button size={isSmall ? 'sm' : 'default'}>Get started</Button>
            </div>
          ) : null}
        </div>
      </header>
    );
  },
);
Navbar.displayName = 'Navbar';

export { Navbar };
